// src/services/EmployeeMergeService.js
import http from './http';
import { wOrgIdMiddleware } from '../utils/commonUtils';
import { CREATED, OK } from '../utils/constant';
import OrganizationService from './OrganizationService';

const EmployeeMergeService = {
  /**
   * Get merge preview data
   * @param {string} url - Base URL
   * @param {string} primaryEmployeeId - Primary employee ID
   * @param {string} secondaryEmployeeId - Secondary employee ID
   */
  getMergePreview: wOrgIdMiddleware(async (url, primaryEmployeeId, secondaryEmployeeId) => {
    try {
      const response = await http.POST(`${url}employees/merge/preview`, {
        primary_employee_id: primaryEmployeeId,
        secondary_employee_id: secondaryEmployeeId,
      });

      // On success, return the inner data; on validation error (422, etc.),
      // return the full response envelope so the caller can read error messages.
      if (response.code === OK && response.data) {
        return response.data;
      }

      return response;
    } catch (error) {
      console.error('Error getting merge preview:', error);
      if (error.status) {
        const resData = error.data;
        if (resData.code && resData.data) {
          return resData;
        }
      }
      throw error;
    }
  }),

  /**
   * Detect conflicts between two employees
   * @param {string} url - Base URL
   * @param {string} primaryEmployeeId - Primary employee ID
   * @param {string} secondaryEmployeeId - Secondary employee ID
   */
  detectConflicts: wOrgIdMiddleware(async (url, primaryEmployeeId, secondaryEmployeeId) => {
    try {
      const response = await http.POST(`${url}employees/merge/detect-conflicts`, {
        primary_employee_id: primaryEmployeeId,
        secondary_employee_id: secondaryEmployeeId,
      });

      // Same pattern as preview: surface full envelope on non-OK codes
      if (response.code === OK && response.data) {
        return response.data;
      }

      return response;
    } catch (error) {
      console.error('Error detecting conflicts:', error);
      if (error.status) {
        const resData = error.data;
        if (resData.code && resData.data) {
          return resData;
        }
      }
      throw error;
    }
  }),

  /**
   * Execute merge operation
   * Note: wOrgIdMiddleware injects 'url' as first parameter, so our parameters start from the second position
   * @param {string} url - Base URL (injected by middleware)
   * @param {string} primaryEmployeeId - Primary employee ID (first argument passed to service)
   * @param {string} secondaryEmployeeId - Secondary employee ID (second argument passed to service)
   * @param {object} conflictResolutions - Conflict resolutions object (third argument passed to service)
   * @param {string} mergeReason - Optional merge reason (fourth argument passed to service)
   * @param {object} visitConflictResolutions - Visit date conflict resolutions { visitId: { action: 'delete'|'change_date', new_date?: 'm/d/Y' } } (fifth argument)
   */
  executeMerge: wOrgIdMiddleware(async (url, primaryEmployeeId, secondaryEmployeeId, conflictResolutions = {}, mergeReason = null, visitConflictResolutions = {}) => {
    try {
      // Debug: Log what we're receiving with full details
      console.log('=== EmployeeMergeService.executeMerge START ===');
      console.log('URL from middleware:', url);
      console.log('Expected orgId from OrganizationService:', OrganizationService.getOrgId());
      console.log('primaryEmployeeId:', primaryEmployeeId, 'Type:', typeof primaryEmployeeId);
      console.log('secondaryEmployeeId:', secondaryEmployeeId, 'Type:', typeof secondaryEmployeeId);
      console.log('conflictResolutions:', conflictResolutions, 'Type:', typeof conflictResolutions);
      console.log('mergeReason:', mergeReason, 'Type:', typeof mergeReason);
      
      // Check if URL is incorrectly using primaryEmployeeId as orgId
      // This happens when OrganizationService.getOrgId() returns the wrong value
      const expectedOrgId = OrganizationService.getOrgId();
      let actualPrimaryEmployeeId = primaryEmployeeId;
      let actualSecondaryEmployeeId = secondaryEmployeeId;
      let actualConflictResolutions = conflictResolutions;
      let actualMergeReason = mergeReason;
      let actualVisitConflictResolutions = visitConflictResolutions;
      
      if (url.includes(primaryEmployeeId) && expectedOrgId && expectedOrgId !== primaryEmployeeId) {
        console.error('⚠️ URL is using primaryEmployeeId as orgId! Parameters are shifted.');
        console.error('⚠️ Attempting to fix parameter order...');
        
        // Reconstruct the URL with correct orgId
        url = `organizations/${expectedOrgId}/`;
        console.log('Fixed URL:', url);
        
        // The parameters are shifted because the middleware used primaryEmployeeId as orgId
        // So: primaryEmployeeId = actual secondaryEmployeeId
        //     secondaryEmployeeId = actual conflictResolutions
        //     conflictResolutions = actual mergeReason
        //     mergeReason = undefined
        
        // We need to get the actual values from the container call
        // But we can't do that here, so we'll throw an error to alert the developer
        console.error('⚠️ Cannot auto-fix: Parameters are shifted. Check OrganizationService.getOrgId()');
        throw new Error('Organization ID mismatch: The middleware is using primaryEmployeeId as orgId. Check OrganizationService.getOrgId() and localStorage.');
      }
      
      console.log('=== EmployeeMergeService.executeMerge END ===');

      // Ensure we're sending the correct data types
      // Validate and convert to strings - but DON'T use String() on objects as it creates "[object Object]"
      if (typeof actualPrimaryEmployeeId !== 'string') {
        console.error('⚠️ primaryEmployeeId is not a string:', actualPrimaryEmployeeId, typeof actualPrimaryEmployeeId);
        if (actualPrimaryEmployeeId && typeof actualPrimaryEmployeeId === 'object') {
          // If it's an object, try to extract the ID
          actualPrimaryEmployeeId = actualPrimaryEmployeeId.id || actualPrimaryEmployeeId.employee_id || actualPrimaryEmployeeId.toString?.() || JSON.stringify(actualPrimaryEmployeeId);
          console.error('⚠️ Extracted primaryEmployeeId from object:', actualPrimaryEmployeeId);
        } else if (actualPrimaryEmployeeId != null) {
          actualPrimaryEmployeeId = String(actualPrimaryEmployeeId);
        } else {
          throw new Error('Primary employee ID is required and must be a string');
        }
      }

      if (typeof actualSecondaryEmployeeId !== 'string') {
        console.error('⚠️ secondaryEmployeeId is not a string:', actualSecondaryEmployeeId, typeof actualSecondaryEmployeeId);
        if (actualSecondaryEmployeeId && typeof actualSecondaryEmployeeId === 'object') {
          // If it's an object, try to extract the ID
          actualSecondaryEmployeeId = actualSecondaryEmployeeId.id || actualSecondaryEmployeeId.employee_id || actualSecondaryEmployeeId.toString?.() || JSON.stringify(actualSecondaryEmployeeId);
          console.error('⚠️ Extracted secondaryEmployeeId from object:', actualSecondaryEmployeeId);
        } else if (actualSecondaryEmployeeId != null) {
          actualSecondaryEmployeeId = String(actualSecondaryEmployeeId);
        } else {
          throw new Error('Secondary employee ID is required and must be a string');
        }
      }

      // Final validation - ensure we have valid UUID strings (32 chars, no dashes)
      const isValidUUID = (id) => typeof id === 'string' && id.length === 32 && /^[a-f0-9]{32}$/i.test(id);
      
      // Check if parameters got swapped (secondaryEmployeeId is an object when it should be a string)
      // This happens when the middleware uses primaryEmployeeId as orgId, shifting all parameters
      if (typeof actualSecondaryEmployeeId === 'object' && typeof actualConflictResolutions === 'string') {
        console.error('⚠️ Parameters appear to be swapped! Attempting to fix...');
        // Parameters are shifted: 
        // - actualPrimaryEmployeeId is actually the secondaryEmployeeId
        // - actualSecondaryEmployeeId is actually conflictResolutions
        // - actualConflictResolutions is actually mergeReason
        // - actualMergeReason is undefined
        
        // We can't fix this without knowing the original values, so throw an error
        throw new Error('Parameters are shifted due to organization ID mismatch. The middleware is using primaryEmployeeId as orgId. Please check OrganizationService.getOrgId() and ensure localStorage has the correct organization_id.');
      }
      
      if (!isValidUUID(actualPrimaryEmployeeId)) {
        console.error('❌ Invalid primaryEmployeeId format:', actualPrimaryEmployeeId);
        throw new Error(`Invalid primary employee ID format: ${actualPrimaryEmployeeId}`);
      }

      if (!isValidUUID(actualSecondaryEmployeeId)) {
        console.error('❌ Invalid secondaryEmployeeId format:', actualSecondaryEmployeeId);
        throw new Error(`Invalid secondary employee ID format: ${actualSecondaryEmployeeId}`);
      }

      const payload = {
        primary_employee_id: actualPrimaryEmployeeId,
        secondary_employee_id: actualSecondaryEmployeeId,
        conflict_resolutions: typeof actualConflictResolutions === 'object' && actualConflictResolutions !== null ? actualConflictResolutions : {},
        merge_reason: actualMergeReason || null,
        visit_conflict_resolutions: typeof actualVisitConflictResolutions === 'object' && actualVisitConflictResolutions !== null ? actualVisitConflictResolutions : {},
      };

      console.log('✅ Sending merge request payload:', payload);

      const response = await http.POST(`${url}employees/merge/execute`, payload);
      
      if ((response.code === OK || response.code === CREATED) && response.data) {
        return response.data;
      } else {
        return response;
      }
    } catch (error) {
      console.error('Error executing merge:', error);
      if (error.status) {
        const resData = error.data;
        if (resData.code && resData.data) {
          return resData;
        }
      }
      throw error;
    }
  }, true),

  /**
   * Get merge history for an employee
   * NOTE: we intentionally do NOT use wOrgIdMiddleware here to avoid
   * accidentally passing organization_id as the employeeId path param.
   */
  getMergeHistory: async (employeeId) => {
    try {
      const orgId = OrganizationService.getOrgId();
      if (!orgId) {
        console.error('getMergeHistory: Organization ID is not set');
        return null;
      }

      const url = `organizations/${orgId}/employees/merge/history/${employeeId}`;
      const response = await http.GET(url);
      
      if (response.code === OK && response.data) {
        return response.data;
      }
      
      return null;
    } catch (error) {
      console.error('Error getting merge history:', error);
      if (error.status) {
        const resData = error.data;
        if (resData.code && resData.data) {
          return resData;
        }
      }
      return null;
    }
  },

  /**
   * Get global merge logs (for Configure > Merge History)
   * @param {Object} params - { orgId?, page, perPage, sort_by, sort_order, search_query, status, employee_id, from_date, to_date }
   * @param {string} params.orgId - Optional; when provided, overrides OrganizationService.getOrgId()
   */
  getMergeLogs: async (params = {}) => {
    try {
      const orgId = params.orgId || OrganizationService.getOrgId();
      if (!orgId) {
        console.error("getMergeLogs: Organization ID is not set");
        return null;
      }
      const { orgId: _o, ...rest } = params;
      const query = Object.fromEntries(
        Object.entries(rest).filter(([, v]) => v != null && v !== "")
      );
      const queryString = new URLSearchParams(query).toString();
      const url = `organizations/${orgId}/employees/merge/logs${queryString ? `?${queryString}` : ""}`;
      const response = await http.GET(url);
      if (response.code === OK && response.data) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.error("Error getting merge logs:", error);
      return null;
    }
  },

  /**
   * Rollback a merge
   * @param {string} url - Base URL
   * @param {string} mergeLogId - Merge log ID
   */
  rollbackMerge: wOrgIdMiddleware(async (url, mergeLogId) => {
    try {
      const response = await http.POST(`${url}employees/merge/rollback/${mergeLogId}`);
      
      if (response.code === OK && response.data) {
        return response.data;
      }
      
      return null;
    } catch (error) {
      console.error('Error rolling back merge:', error);
      if (error.status) {
        const resData = error.data;
        if (resData.code && resData.data) {
          return resData;
        }
      }
      throw error;
    }
  }, true),
};

export default EmployeeMergeService;

